import { create } from 'zustand';

/**
 * Theme + premium-tier flag.
 *
 * Two surfaces:
 *
 *   1. Legacy `theme` ('dark' | 'light' | 'console') — locked to
 *      'console' for now. Dark and light never reached visual parity
 *      with the console palette; until they do, every surface
 *      renders in console mode regardless of stored preference.
 *      The Theme type and toggle() API are preserved so call sites
 *      compile; toggle() is a no-op.
 *
 *   2. `cockpitV2: boolean` — the premium-tier feature flag per
 *      docs/strategy/EMSPCR_UI_BUILD_SPEC_V1.md §8. When true, the
 *      <html> element's data-theme is "cockpit-v2"; when false,
 *      "console". Defaults false (legacy console theme renders).
 *
 *      Per-user opt-in eventually surfaces at /admin → User
 *      Preferences → "Premium theme (preview)". Override on a dev
 *      rig by appending `?theme=cockpit-v2` to any URL — the boot
 *      probe in init() reads the URL and persists the flag to
 *      localStorage. To turn off: `?theme=console`.
 *
 *      Fallback: if the v2 stylesheet fails to load (offline-rig
 *      boot, CDN miss — unlikely since self-hosted), a probe checks
 *      for the --bezel-light-on token after a 2 s timeout and
 *      reverts to console if absent.
 */
export type Theme = 'dark' | 'light' | 'console';

const COCKPIT_V2_LS_KEY = 'emspcr.cockpit-v2.enabled';

interface ThemeState {
  theme: Theme;
  cockpitV2: boolean;
  setTheme: (t: Theme) => void;
  toggle: () => void;
  setCockpitV2: (enabled: boolean) => void;
  init: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: 'console',
  cockpitV2: false,    // v1 console is the default again. v2 is opt-in
                       // via ?theme=cockpit-v2 or localStorage so we
                       // can iterate without making everyone the
                       // guinea-pig. Toggle UI restored in /admin
                       // (see PremiumThemeToggle in AdminConsolePage).

  init() {
    // Always console legacy attribute — clear any stale preference
    // written by an earlier build that exposed the cycle toggle.
    try { localStorage.removeItem('emspcr-theme'); } catch { /* ignore */ }

    // Resolve cockpit-v2 flag in this priority:
    //   1. URL query param `?theme=cockpit-v2` / `?theme=console`
    //      — one-shot dev override; persists on read so a refresh
    //      keeps it.
    //   2. localStorage (per-user opt-in via /admin toggle).
    //   3. Default: false (v1 console).
    let cockpitV2 = false;
    try {
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const paramTheme = url.searchParams.get('theme');
        if (paramTheme === 'cockpit-v2') {
          cockpitV2 = true;
          localStorage.setItem(COCKPIT_V2_LS_KEY, 'true');
        } else if (paramTheme === 'console') {
          cockpitV2 = false;
          localStorage.setItem(COCKPIT_V2_LS_KEY, 'false');
        } else {
          cockpitV2 = localStorage.getItem(COCKPIT_V2_LS_KEY) === 'true';
        }
      }
    } catch {
      // Private-browsing or quota — fall back to default off.
    }

    applyTheme(cockpitV2);
    set({ theme: 'console', cockpitV2 });

    // Post-load probe: if v2 was requested but its sentinel token
    // didn't deliver, gracefully revert to v1 so the user never sees
    // a broken render.
    if (cockpitV2 && typeof window !== 'undefined') {
      window.setTimeout(() => {
        try {
          const root = document.documentElement;
          const sentinel = getComputedStyle(root)
            .getPropertyValue('--bezel-light-on')
            .trim();
          if (!sentinel) {
            // eslint-disable-next-line no-console
            console.warn('[theme] cockpit-v2 sentinel not present; reverting to console.');
            applyTheme(false);
            try { localStorage.setItem(COCKPIT_V2_LS_KEY, 'false'); } catch { /* ignore */ }
            set({ cockpitV2: false });
          }
        } catch { /* ignore */ }
      }, 2000);
    }
  },

  setTheme() {
    // Locked. Always console (only the v1/v2 flag varies).
    applyTheme(get().cockpitV2);
    set({ theme: 'console' });
  },

  toggle() {
    applyTheme(get().cockpitV2);
    set({ theme: 'console' });
  },

  setCockpitV2(enabled: boolean) {
    applyTheme(enabled);
    try { localStorage.setItem(COCKPIT_V2_LS_KEY, enabled ? 'true' : 'false'); } catch { /* ignore */ }
    set({ cockpitV2: enabled });
  },
}));

/**
 * HereToo is now the product appearance — one look across EMSPCR and HereToo,
 * per the unification decision. The console and cockpit-v2 themes still exist
 * and still work; they are reachable with ?theme=console / ?theme=cockpit-v2
 * so a surface can be compared against the old skin while the sweep runs.
 *
 * Light is the default because HereToo's canvas is white and the clinical
 * status tokens are struck for it. Dark is a viewer preference, not a skin.
 */
const HT_MODE_LS_KEY = 'emspcr.heretoo.mode';

function applyTheme(cockpitV2: boolean): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // Remove BOTH legacy theme classes. `.light`'s rules in index.css forcibly
  // remap Tailwind dark-slate backgrounds to white, which would steamroll
  // whichever theme is active; data-theme is the only switch.
  root.classList.remove('dark');
  root.classList.remove('light');

  // Escape hatches, for comparing a converted surface against the old skin.
  let override: string | null = null;
  try {
    const q = new URLSearchParams(window.location.search).get('theme');
    if (q === 'console' || q === 'cockpit-v2') override = q;
  } catch { /* ignore */ }

  if (override) {
    root.setAttribute('data-theme', override);
    root.style.colorScheme = 'dark';
    return;
  }

  let mode: string | null = null;
  try { mode = localStorage.getItem(HT_MODE_LS_KEY); } catch { /* ignore */ }
  const dark = mode === 'dark';
  root.setAttribute('data-theme', dark ? 'heretoo-dark' : 'heretoo');
  root.style.colorScheme = dark ? 'dark' : 'light';
  // cockpitV2 no longer selects a theme; kept in the signature because the
  // store's callers still pass it and the flag still gates v2-only components.
  void cockpitV2;
}
